<script lang="ts">
  // Overlay shown on a cell that's in archive playback mode.
  // Provides: timeline scrubber, speed buttons, pause/play, back-to-live.

  type Props = {
    /** ms epoch — start of the originally-requested range. */
    rangeStartMs: number;
    /** ms epoch — end of range. */
    rangeEndMs: number;
    /** ms epoch — start of CURRENT playback task (after seeks). */
    seekStartMs: number;
    /** Reference to the <video> element so we can read currentTime, set
        playbackRate, and call pause/play. Provided by parent. */
    video: HTMLVideoElement | null;
    /** Current rate (1 = realtime). */
    rate: number;
    /** Notify parent of a seek (frac 0..1 of full range). */
    onseek: (frac: number) => void | Promise<void>;
    /** Notify parent of a rate change (also we apply playbackRate locally). */
    onrate: (newRate: number) => void;
    /** User clicked back-to-live. */
    onbacktolive: () => void;
  };
  let { rangeStartMs, rangeEndMs, seekStartMs, video, rate, onseek, onrate, onbacktolive }: Props =
    $props();

  let dragging = $state(false);
  let dragFrac = $state(0);
  let progressFrac = $state(0);
  let curMs = $state(seekStartMs);
  let elapsedSec = $state(0);
  let paused = $state(false);

  let bar: HTMLDivElement | undefined = $state();
  let tickHandle: ReturnType<typeof setInterval> | null = null;

  function pad(n: number): string {
    return String(n).padStart(2, '0');
  }
  function fmtTime(ms: number): string {
    const d = new Date(ms);
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
  function fmtDur(s: number): string {
    s = Math.max(0, Math.floor(s));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h}:${pad(m)}:${pad(sec)}`;
  }

  function tick(): void {
    if (!video || dragging) return;
    elapsedSec = video.currentTime || 0;
    curMs = seekStartMs + elapsedSec * 1000;
    const totalSec = (rangeEndMs - rangeStartMs) / 1000;
    if (totalSec <= 0) return;
    const elapsedFromStart = (seekStartMs - rangeStartMs) / 1000 + elapsedSec;
    progressFrac = Math.max(0, Math.min(1, elapsedFromStart / totalSec));
    paused = video.paused;
  }

  $effect(() => {
    if (tickHandle) clearInterval(tickHandle);
    tickHandle = setInterval(tick, 500);
    return () => {
      if (tickHandle) clearInterval(tickHandle);
    };
  });

  function fracFromEvent(ev: MouseEvent | TouchEvent): number {
    if (!bar) return 0;
    const rect = bar.getBoundingClientRect();
    const x = 'touches' in ev ? ev.touches[0]!.clientX : ev.clientX;
    return Math.max(0, Math.min(1, (x - rect.left) / rect.width));
  }
  function startDrag(ev: MouseEvent | TouchEvent): void {
    ev.preventDefault();
    dragging = true;
    dragFrac = fracFromEvent(ev);
  }
  function moveDrag(ev: MouseEvent | TouchEvent): void {
    if (!dragging) return;
    dragFrac = fracFromEvent(ev);
  }
  function endDrag(): void {
    if (!dragging) return;
    dragging = false;
    onseek(dragFrac);
  }

  function setSpeed(s: number): void {
    if (video) video.playbackRate = s;
    onrate(s);
  }

  function togglePause(): void {
    if (!video) return;
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  }

  const SPEEDS = [0.5, 1, 2, 4, 8];
  const visibleFrac = $derived(dragging ? dragFrac : progressFrac);
</script>

<svelte:window onmousemove={moveDrag} onmouseup={endDrag} ontouchmove={moveDrag} ontouchend={endDrag} />

<div class="ctrl" class:dragging>
  <div class="row top">
    <button class="back" type="button" onclick={onbacktolive} title="Live">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round">
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
    <button class="play" type="button" onclick={togglePause} title={paused ? 'Воспроизвести' : 'Пауза'}>
      {#if paused}
        <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="6 4 20 12 6 20" /></svg>
      {:else}
        <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
      {/if}
    </button>
    <span class="time">{fmtTime(curMs)} · {fmtDur(elapsedSec)}</span>
    <div
      class="bar" bind:this={bar}
      onmousedown={startDrag}
      ontouchstart={startDrag}
      role="slider" tabindex="0"
      aria-valuemin="0" aria-valuemax="1" aria-valuenow={visibleFrac.toFixed(3)}
    >
      <div class="progress" style:width="{visibleFrac * 100}%"></div>
      <div class="thumb" style:left="{visibleFrac * 100}%"></div>
    </div>
  </div>
  <div class="row bottom">
    <div class="speeds">
      {#each SPEEDS as s}
        <button type="button" class:active={rate === s} onclick={() => setSpeed(s)}>
          {s === 0.5 ? '×½' : `×${s}`}
        </button>
      {/each}
    </div>
    <div class="range">{fmtTime(rangeStartMs)} → {fmtTime(rangeEndMs)}</div>
  </div>
</div>

<style>
  .ctrl {
    position: absolute; left: 8px; right: 8px; bottom: 8px;
    display: flex; flex-direction: column; gap: 6px;
    background: rgba(0,0,0,.55);
    padding: 6px 8px; border-radius: 8px;
    backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
  }
  .row { display: flex; align-items: center; gap: 8px; }
  .back, .play {
    width: 26px; height: 26px; border-radius: 50%;
    background: rgba(255,255,255,.1); color: #fff;
    display: grid; place-items: center;
  }
  .back :global(svg), .play :global(svg) { width: 14px; height: 14px; }
  .back:hover, .play:hover { background: rgba(255,255,255,.22); }
  .time {
    font-size: 10px; color: rgba(255,255,255,.85);
    font-family: ui-monospace, "SF Mono", Menlo, monospace;
    min-width: 130px; font-variant-numeric: tabular-nums;
  }
  .bar {
    flex: 1; height: 10px;
    background: rgba(255,255,255,.22);
    border-radius: 5px; cursor: pointer; position: relative;
    transition: height .12s;
  }
  .bar:hover, .ctrl.dragging .bar { height: 13px; }
  .progress {
    position: absolute; left: 0; top: 0; bottom: 0;
    background: linear-gradient(90deg, rgba(255,140,0,.95), rgba(255,180,40,1));
    border-radius: 5px; pointer-events: none; min-width: 4px;
    box-shadow: 0 0 8px rgba(255,140,0,.4);
  }
  .thumb {
    position: absolute; top: 50%; transform: translate(-50%, -50%);
    width: 16px; height: 16px; border-radius: 50%;
    background: #fff; pointer-events: none;
    box-shadow: 0 0 0 3px rgba(255,140,0,.7), 0 2px 6px rgba(0,0,0,.4);
  }
  .ctrl.dragging .thumb { transform: translate(-50%, -50%) scale(1.15); }
  .speeds { display: flex; gap: 3px; }
  .speeds button {
    font-size: 10px; padding: 2px 7px; border-radius: 4px;
    color: rgba(255,255,255,.75);
    background: rgba(255,255,255,.08);
    font-variant-numeric: tabular-nums;
  }
  .speeds button.active { background: rgba(255,165,0,.9); color: #000; font-weight: 600; }
  .speeds button:hover:not(.active) { background: rgba(255,255,255,.18); color: #fff; }
  .range { margin-left: auto; font-size: 9px; color: #bbb; font-variant-numeric: tabular-nums; }
</style>
